import { useCallback, useEffect, useRef, useState } from "react";
import { diariesApi, nutritionApi, fitnessApi } from "../../../api";
import { MISSION_ACTIVITY_EVENT } from "../../../missionEvents";
import { asRows, growthRecords, type GrowthDiary } from "./recordsModel";
export function useGrowthRecords(
  suppliedDiaries?: GrowthDiary[],
  mealDates?: string[],
) {
  const [data, setData] = useState<{
    notes: GrowthDiary[];
    meals: any[];
    sessions: any[];
  }>({ notes: [], meals: [], sessions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sequence = useRef(0);
  const dateKey = mealDates?.join(",") || "";
  const hasDiaries = suppliedDiaries !== undefined;
  const load = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    const results = await Promise.allSettled([
      hasDiaries ? Promise.resolve({ data: [] }) : diariesApi.getAll(),
      dateKey
        ? Promise.all(
            dateKey.split(",").map((day) => nutritionApi.getMeals(day)),
          ).then((responses) => ({
            data: responses.flatMap((r) => asRows(r.data.meals)),
          }))
        : nutritionApi.searchMealHistory(),
      fitnessApi.getExerciseSessions(),
    ]);
    if (request !== sequence.current) return;
    const names = ["笔记", "饮食", "运动"];
    setError(
      results
        .flatMap((r, i) => (r.status === "rejected" ? [names[i]] : []))
        .join("、"),
    );
    const rows = results.map((r) =>
      r.status === "fulfilled" ? asRows(r.value.data) : [],
    );
    setData({ notes: rows[0], meals: rows[1], sessions: rows[2] });
    setLoading(false);
  }, [hasDiaries, dateKey]);
  useEffect(() => {
    load();
    const requests = sequence;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(load, 150);
    };
    window.addEventListener(MISSION_ACTIVITY_EVENT, refresh);
    return () => {
      requests.current++;
      clearTimeout(timer);
      window.removeEventListener(MISSION_ACTIVITY_EVENT, refresh);
    };
  }, [load]);
  return {
    records: growthRecords(
      suppliedDiaries || data.notes,
      data.meals,
      data.sessions,
    ),
    loading,
    error: error ? `${error}记录加载失败` : "",
    reload: load,
  };
}
